import { DeviceType } from "src/types/device";
import { debugLog } from ".";

const GEOLOCATION_TIMEOUT = 5000; // 5 seconds
const DEVICE_ID_KEY = "deviceId";
const IP_FETCH_TIMEOUT = 3000; // 3 seconds

const getPublicIpAddress = async (): Promise<string> => {
  const ipServices = [
    "https://api.ipify.org?format=json",
    "https://ipapi.co/json/",
    "https://httpbin.org/ip",
  ];

  for (const service of ipServices) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), IP_FETCH_TIMEOUT);

      const response = await fetch(service, {
        signal: controller.signal,
        headers: { Accept: "application/json" },
      });

      clearTimeout(timeoutId);

      if (!response.ok) continue;

      const data = (await response.json()) as {
        ip: string;
        origin: string;
        query: string;
      };

      // Different services return IP in different formats
      const ip = data.ip ?? data.origin ?? data.query;

      if (ip && typeof ip === "string") {
        return ip;
      }
    } catch (error) {
      debugLog(`Failed to fetch IP from ${service}: ${JSON.stringify(error)}`);
      continue;
    }
  }

  return "0.0.0.0"; // Fallback
};

const getBrowserInfo = () => {
  const { userAgent } = navigator;
  let name = "Unknown";
  let version = "Unknown";

  const browserPatterns = {
    Chrome: /Chrome\/([0-9.]+)/,
    Firefox: /Firefox\/([0-9.]+)/,
    Safari: /Version\/([0-9.]+).*Safari/,
    Edge: /Edg\/([0-9.]+)/,
    IE: /Trident.*rv:([0-9.]+)/,
    Opera: /OPR\/([0-9.]+)/,
  };

  for (const [browser, pattern] of Object.entries(browserPatterns)) {
    const match = userAgent.match(pattern);
    if (match) {
      name = browser;
      version = match[1];
      break;
    }
  }

  return { name, version };
};

const getGeolocation = (): Promise<{ latitude: number; longitude: number }> =>
  new Promise((resolve) => {
    if (!navigator.geolocation) {
      return resolve({ latitude: 0, longitude: 0 });
    }

    const timeoutId = setTimeout(() => {
      resolve({ latitude: 0, longitude: 0 });
    }, GEOLOCATION_TIMEOUT);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        clearTimeout(timeoutId);
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      () => {
        clearTimeout(timeoutId);
        resolve({ latitude: 0, longitude: 0 });
      },
      {
        timeout: GEOLOCATION_TIMEOUT,
        enableHighAccuracy: false,
        maximumAge: 300000, // 5 minutes
      },
    );
  });

interface UserAgentData {
  readonly platform: string;
}

const getPlatform = (): string => {
  if ("userAgentData" in navigator && navigator.userAgentData) {
    return (navigator.userAgentData as UserAgentData).platform;
  }

  const ua = navigator.userAgent.toLowerCase();

  if (ua.includes("windows")) {
    return "Windows";
  } else if (ua.includes("macintosh") || ua.includes("mac os")) {
    return "macOS";
  } else if (ua.includes("linux")) {
    return "Linux";
  } else if (ua.includes("iphone")) {
    return "iOS";
  } else if (ua.includes("ipad")) {
    return "iPadOS";
  } else if (ua.includes("android")) {
    return "Android";
  }

  return "Unknown";
};

const generateFingerprint = (): string => {
  try {
    const components = [
      navigator.userAgent,
      navigator.language,
      window.screen.width,
      window.screen.height,
      window.screen.colorDepth,
      new Date().getTimezoneOffset(),
      navigator.hardwareConcurrency || 0,
      navigator.maxTouchPoints || 0,
    ];

    const fingerprintString = components.join("|");
    return btoa(fingerprintString);
  } catch (error) {
    debugLog(error);
    return "fallback_fingerprint";
  }
};

const getOrCreateDeviceId = (): string => {
  try {
    const existingId = window.localStorage.getItem(DEVICE_ID_KEY);
    if (existingId) {
      return existingId;
    }

    const newId = crypto.randomUUID();
    window.localStorage.setItem(DEVICE_ID_KEY, newId);
    return newId;
  } catch (error) {
    debugLog(error);
    return `fallback_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }
};

export const getDeviceData = async (): Promise<DeviceType> => {
  const deviceId = getOrCreateDeviceId();
  const fingerprint = generateFingerprint();
  const browser = getBrowserInfo();
  const geolocation = await getGeolocation();
  const platform = getPlatform();

  const deviceData: DeviceType = {
    ipAddress: await getPublicIpAddress(),
    deviceId,
    fingerprint,
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform,
    geolocation,
    screen: {
      width: window.screen.width,
      height: window.screen.height,
    },
    browser,
  };

  return deviceData;
};
